import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ReportPreferenceService } from './report-preference.service';
import { FavouriteConfig } from '../shared/utils/report-state.types';

// Re-export so consumers only need to import from this file.
export { FavouriteConfig } from '../shared/utils/report-state.types';

/**
 * Lightweight data stored per favourite report.
 * Never store large API responses or table data here â€” only identifiers,
 * display metadata, and optional named configuration snapshots.
 */
export interface FavouriteReportEntry {
  /** Report slug (unique within its module). */
  slug: string;
  /** Module key, e.g. 'fee-management', 'hrms', 'admin', 'teacher', 'transport'. */
  module: string;
  /** Human-readable module label shown on the Favourites card. */
  moduleLabel: string;
  /** Report display name. */
  name: string;
  /** Full absolute Angular route path, e.g. '/nora/fee-management/reports/collection-summary'. */
  routePath: string;
  /** Short description. */
  description: string;
  /**
   * Named configuration snapshots for this favourite.
   * Each config captures the full UI state at the time of saving.
   * When no configs are present the report opens using its last-used state.
   */
  configs?: FavouriteConfig[];
}

/**
 * FavouriteReportService
 * â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
 * Reactive facade over `ReportPreferenceService` for managing favourite reports.
 *
 * Favourites are intentionally preserved across logout and restored when the
 * same user logs back in from the same browser.  Storage is user-scoped by
 * userId so different users on the same device cannot see each other's data.
 *
 * Storage key: `reportFavourites:{userId}`
 */
@Injectable({ providedIn: 'root' })
export class FavouriteReportService {
  private readonly _favourites$ = new BehaviorSubject<FavouriteReportEntry[]>([]);

  constructor(private prefSvc: ReportPreferenceService) {
    this._load();
  }

  // â”€â”€ Internal helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  private _currentUserId(): string | null {
    return this.prefSvc.getCurrentUserId();
  }

  private _load(): void {
    const uid = this._currentUserId();
    if (!uid) { this._favourites$.next([]); return; }
    this._favourites$.next(this.prefSvc.getFavourites(uid));
  }

  private _push(entries: FavouriteReportEntry[]): void {
    const uid = this._currentUserId();
    if (!uid) return;
    this._favourites$.next(entries);
    // Persist the entire list via the central preference service.
    for (const entry of entries) {
      this.prefSvc.saveFavourite(uid, entry);
    }
    // If the list shrank (an item was removed), write the new list directly.
    const stored = this.prefSvc.getFavourites(uid);
    if (stored.length !== entries.length) {
      // Re-sync: remove entries that are no longer in our list.
      const slugModuleSet = new Set(entries.map(e => `${e.slug}|${e.module}`));
      stored
        .filter(f => !slugModuleSet.has(`${f.slug}|${f.module}`))
        .forEach(f => this.prefSvc.removeFavourite(uid, f.slug, f.module));
    }
  }

  // â”€â”€ Public API â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  /** Observable stream of all favourite entries for the current user. */
  getAll(): Observable<FavouriteReportEntry[]> {
    return this._favourites$.asObservable();
  }

  /** Synchronous snapshot of current favourites. */
  getSnapshot(): FavouriteReportEntry[] {
    return this._favourites$.value;
  }

  /** Returns true if the given report is already a favourite. */
  isFavourite(slug: string, module: string): boolean {
    return this._favourites$.value.some(f => f.slug === slug && f.module === module);
  }

  /**
   * Toggle favourite status for a report.
   * @returns 'added' | 'removed'
   */
  toggle(entry: FavouriteReportEntry): 'added' | 'removed' {
    const uid = this._currentUserId();
    const current = this._favourites$.value;
    const idx = current.findIndex(f => f.slug === entry.slug && f.module === entry.module);
    const action: 'added' | 'removed' = idx >= 0 ? 'removed' : 'added';

    if (action === 'removed') {
      const next = current.filter((_, i) => i !== idx);
      this._favourites$.next(next);
      if (uid) this.prefSvc.removeFavourite(uid, entry.slug, entry.module);
    } else {
      const next = [...current, { ...entry }];
      this._favourites$.next(next);
      if (uid) this.prefSvc.saveFavourite(uid, { ...entry });
    }
    return action;
  }

  /**
   * Re-load favourites from storage for the currently-logged-in user.
   * Call in component ngOnInit to pick up changes after login or user-switch.
   */
  reload(): void {
    this._load();
  }

  // â”€â”€ Named configuration snapshots â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  /** Add a named config to an existing favourite (no-op if not yet a favourite). */
  addConfig(slug: string, module: string, config: FavouriteConfig): void {
    const uid = this._currentUserId();
    const current = this._favourites$.value;
    const idx = current.findIndex(f => f.slug === slug && f.module === module);
    if (idx < 0) return;
    const entry = current[idx];
    const existing = entry.configs ?? [];
    if (existing.some(c => c.name.trim().toLowerCase() === config.name.trim().toLowerCase())) return;
    const updated = { ...entry, configs: [...existing, config] };
    const next = current.map((f, i) => (i === idx ? updated : f));
    this._favourites$.next(next);
    if (uid) this.prefSvc.saveFavourite(uid, updated);
  }

  /** Replace the name or state of an existing named config. */
  updateConfig(
    slug: string,
    module: string,
    configId: string,
    patch: Partial<Pick<FavouriteConfig, 'name' | 'state'>>
  ): void {
    const uid = this._currentUserId();
    const current = this._favourites$.value;
    const idx = current.findIndex(f => f.slug === slug && f.module === module);
    if (idx < 0) return;
    const now = Date.now();
    const configs = (current[idx].configs ?? []).map(c =>
      c.id === configId ? { ...c, ...patch, updatedAt: now } : c
    );
    const updated = { ...current[idx], configs };
    const next = current.map((f, i) => (i === idx ? updated : f));
    this._favourites$.next(next);
    if (uid) this.prefSvc.saveFavourite(uid, updated);
  }

  /** Remove a single named config (the favourite entry remains). */
  removeConfig(slug: string, module: string, configId: string): void {
    const uid = this._currentUserId();
    const current = this._favourites$.value;
    const idx = current.findIndex(f => f.slug === slug && f.module === module);
    if (idx < 0) return;
    const configs = (current[idx].configs ?? []).filter(c => c.id !== configId);
    const updated = { ...current[idx], configs };
    const next = current.map((f, i) => (i === idx ? updated : f));
    this._favourites$.next(next);
    if (uid) this.prefSvc.saveFavourite(uid, updated);
  }

  /** Look up a named config by its ID across all favourites. */
  getConfigById(configId: string): FavouriteConfig | null {
    for (const f of this._favourites$.value) {
      const found = (f.configs ?? []).find(c => c.id === configId);
      if (found) return found;
    }
    return null;
  }

  /** Return all configs for a specific favourite. */
  getConfigs(slug: string, module: string): FavouriteConfig[] {
    const entry = this._favourites$.value.find(
      f => f.slug === slug && f.module === module
    );
    return entry?.configs ?? [];
  }
}
